import React from 'react'
import { range, isNil } from 'ramda'
import { FilterTextField, FilterSelector, FilterDate, FilterMonth, WeekFilterButton, currentFromFilter, FilterAutocompleteField, FilterProps } from './Filter'
import { weekdayToDescription } from '../../../utils/days'
import { ADMIN, HEADSALES, MANAGER, Role, STAFF, VENDOR } from '../../../models/User'
import { ButtonGroup, FormControl, InputLabel, ListItemIcon, ListItemText, MenuItem, Select } from '@mui/material'
import Client from '../../../models/Client'
import moment from 'moment'
import fetchApi, { getToken } from '../../../utils/fetchApi'
import { useTranslation } from 'react-i18next'
import { statusOrderNamesTranslated } from '../../Client/Orders'
import { t } from 'i18next'
import { complaintAssignedTo, complaintAssignedToDescription, complaintInternalAssignedTo, complaintInternalAssignedToDescription, complaintOwnerOpenBy, complaintOwnerOpenByDescription, complaintStatusTypeToDescription, complaintStatusTypes } from '../../Client/Complaints'
import { clientStatus, clientStatusToDescription } from '../../Administrator/Clients'
import Person from '../../../models/Person'
import { roleToPersonType } from '../../Client/Profile'
import { receiptTypeToDescription, receiptTypesWithBounced } from '../../GeneralDesc'
import { prepaymentStatus, prepaymentStatusToDescription } from '../../Client/Prepayments'
import Invoice from '../../../models/Invoice'
import { formattedCurrency } from '../../../utils/StringUtils'
import Order from '../../../models/Order'
import Remittance from '../../../models/Remittance'
import { productStatusTypeToDescription, productStatusTypes } from '../../Administrator/Products/Products'
import ProductBrand from '../../../models/ProductBrand'
import ProductSubgroup from '../../../models/ProductSubgroup'
import ProductGroup from '../../../models/ProductGroup'
import ProductSection from '../../../models/ProductSection'
import { SelectorProps } from '../../Selector'
import { useThemeContext } from '../../../theme/ThemeContext'
import { PreorderOwners, preorderOwners, preorderOwnersToDescription, preorderStatus, preorderStatusToDescription } from '../../Utils/CommonConst'
import Vendor from '../../../models/Vendor'

export const searchClients = (search: string, role?: Role, roleId?: Number) =>
  fetchApi(getToken(), 'GET', `clients?nombre=${search}${role && role === VENDOR ? `&codigo_vendedor=${roleId}` : ""}`)
    .then(response => response.clients)

export const searchVendors = (search: string) =>
  fetchApi(getToken(), 'GET', `vendors?nombre=${search}`)
    .then(response => response.vendors)

export const searchPeople = (search: string) =>
  fetchApi(getToken(), 'GET', `people?fullName=${search}&personType=${roleToPersonType(ADMIN)},${roleToPersonType(STAFF)},${roleToPersonType(HEADSALES)},${roleToPersonType(MANAGER)},${roleToPersonType(VENDOR)}`)
    .then(response => response.people)

export const searchInvoices = (search: string) =>
  fetchApi(getToken(), 'GET', `invoices?invoiceId=${search}`)
    .then(response => response.invoices)

export const searchOrders = (search: string) =>
  fetchApi(getToken(), 'GET', `orders?orderId=${search}`)
    .then(response => response.orders)

export const searchRemittances = (search: string) =>
  fetchApi(getToken(), 'GET', `remittances?remittanceId=${search}`)
    .then(response => response.remittances)

export const searchBrands = (search: string) =>
  fetchApi(getToken(), 'GET', `productBrands?description=${search}`)
    .then(response => response.productBrands.map((productBrand: ProductBrand) => { return { ...productBrand, brandName: productBrand.description } }))

export const searchSections = (search: string) =>
  fetchApi(getToken(), 'GET', `productSections?description=${search}`)
    //.then(response => response.productSections)
    .then(response => response.productSections.map((productSection: ProductSection) => { return { ...productSection, sectionName: productSection.description } }))

export const searchSubgroups = (search: string) =>
  fetchApi(getToken(), 'GET', `productSubgroups?description=${search}`)
    //    .then(response => response.productSubgroups)
    .then(response => response.productSubgroups.map((productSubgroup: ProductSubgroup) => { return { ...productSubgroup, subgroupName: productSubgroup.description } }))

export const searchGroups = (search: string) =>
  fetchApi(getToken(), 'GET', `productGroups?description=${search}`)
    //    .then(response => response.productGroups)
    .then(response => response.productGroups.map((productGroup: ProductGroup) => { return { ...productGroup, groupName: productGroup.description } }))

export const FilterDocumentNumber = (props) => <FilterTextField name="documentNumber" label="DNI" {...props} />

export const FilterClientName = (props) => <FilterAutocompleteField<Client>
  name='clientName'
  search={searchClients}
  selectLabel={client => `${client.nombre} [${client.codigo_cliente}] ${client.nombrefantasia}`}
  {...props}
/>

export const FilterVendorName = (props) => <FilterAutocompleteField<Vendor>
  name='vendorName'
  search={searchVendors}
  selectLabel={vendor => `${vendor.nombre} [${vendor.codigo_vendedor}]`}
  {...props}
/>

export const FilterPersonName = (props) => <FilterAutocompleteField<Person>
  name='id'
  search={searchPeople}
  selectLabel={person => `${person.fullName}`}
  {...props}
/>

export const FilterInvoices = (props) => <FilterAutocompleteField<Invoice>
  name='id_factura'
  search={searchInvoices}
  selectLabel={invoice => `${invoice.id_factura} ${moment(invoice.fecha).tz("America/Argentina/Buenos_Aires").format('DD/MM/YYYY')} ${formattedCurrency(invoice.importe_total_comprobante)}`}
  {...props}
/>

export const FilterOrders = (props) => <FilterAutocompleteField<Order>
  name='id_pedido'
  search={searchOrders}
  selectLabel={order => `${order.id_pedido.trimEnd()} ${moment(order.fecha).tz("America/Argentina/Buenos_Aires").format('DD/MM/YYYY')} ${formattedCurrency(order.importe_total_comprobante)}`}
  {...props}
/>

export const FilterRemittances = (props) => <FilterAutocompleteField<Remittance>
  name='id_remito'
  search={searchRemittances}
  selectLabel={remittance => `${remittance.id_remito} ${moment(remittance.fecha).tz("America/Argentina/Buenos_Aires").format('DD/MM/YYYY')} ${remittance.pedido.id_pedido.trimEnd()} `}
  {...props}
/>


export const FilterBrand: React.FC<FilterProps & SelectorProps> = (props) => {
  const { theme } = useThemeContext()

  return (
    <FormControl fullWidth={props.fullwidth} margin={props.margin} variant={props.variant}>
      <InputLabel id={'select'} style={{ padding: "0px 8px", backgroundColor: theme.palette.info.main }} required={props.required}>{props.label}</InputLabel>
      <Select
        style={{ ...props.style }}
        key={props.label}
        id={'select'}
        name={props.name}
        value={props.value || (props.filters && props.filters[props.name])}
        inputProps={{ style: { borderColor: 'red' } }}
        onChange={event => { props.onChange && props.onChange(props.name, event.target.value) }}>

        {props.menuItems && props.menuItems.map(((item) =>
          <MenuItem value={item.id} id={(item.id)} key={(item.id)}
            style={{ fontWeight: 400 }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <ListItemIcon>
                <img
                  loading="lazy"
                  width="100"
                  //srcSet={`../img/brands/${item.code}.png 2x`}
                  //src={`../img/brands/${item.code}.png`}
                  src={require(`/public/img/brands/${item.code}.png`)}
                  alt={`${item.code}.png`}
                />
              </ListItemIcon>
              <ListItemText primary={item.description} style={{ marginLeft: '5px' }}>
              </ListItemText>
            </div>
          </MenuItem>))}
      </Select>
    </FormControl>
  )
}

export const FilterPreorderStuff: React.FC<SelectorProps & FilterProps> = (props) => {
  const { theme } = useThemeContext()
  return (
    <FormControl fullWidth={props.fullwidth} margin={props.margin} variant={props.variant}>
      <InputLabel id={'select'} style={{ padding: "0px 8px", backgroundColor: theme.palette.info.main }} required={props.required}>{props.label}</InputLabel>

      <Select
        key={props.label}
        style={{ ...props.style }}
        id={'select'}
        name={props.name}
        value={props.value || (props.filters && props.filters[props.name]) || null}
        onChange={event => { props.onChange && props.onChange(props.name, event.target.value) }}>
        {props.menuItems && props.menuItems.map(((item) =>
          <MenuItem value={item.id} id={(item.id)} key={(item.id)}
            style={{ fontWeight: 400 }}>
            <ListItemText primary={item.description}>
            </ListItemText>
          </MenuItem>))}
      </Select>
    </FormControl>
  )
}

export const FilterNumber = (props) => <FilterTextField sx={{
  '& number': {
    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
      "-webkit-appearance": "none",
      margin: 0
    }
  },
  ' & input': {
    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
      "-webkit-appearance": "none",
      margin: 0
    }
  }
}} type="number" name="number" {...props} />
export const FilterTel = (props) => <FilterTextField type="tel" name="number" {...props} />
export const FilterText = (props) => <FilterTextField name="text" {...props} />

export const FilterEnableDisable = (props) => {
  const value = props.filters[props.name]
  const { t } = useTranslation('others')
  return <FilterSelector name="enableDisable"  {...props}
    value={isNil(value) ? "" : Boolean(value)}
    textAdapter={(_value) => Boolean(_value) ? t("yes") : t("no")}
    menuItems={[true, false]}
  />
}

export const FilterStatusOrder = (props) =>
  <FilterSelector name="status" {...props}
    menuItems={statusOrderNamesTranslated()}
  //    multiple
  />

export const statusPreorderNamesTranslated = (value: string): string =>
  t(`preorder:${preorderStatusToDescription(value)}`)


export const FilterStatusPreorder = (props) =>
  <FilterSelector name="statusPO" {...props}
    menuItems={preorderStatus}
    textAdapter={statusPreorderNamesTranslated}
  //    multiple
  />

export const FilterPreorderOwners = (props) =>
  <FilterSelector name="owner" {...props}
    menuItems={preorderOwners}
    textAdapter={PreorderOwnersNameTranslated}
  //    multiple
  />

const PreorderOwnersNameTranslated = (value: PreorderOwners): string =>
  t(`preorder:${preorderOwnersToDescription(value)}`)

const complaintStatusTypeTranslated = (value: string): string =>
  t(`complaint:${complaintStatusTypeToDescription(value)}`)

export const FilterStatusComplaint = (props) =>
  <FilterSelector name="status" {...props}
    menuItems={complaintStatusTypes}
    textAdapter={complaintStatusTypeTranslated}
  //    multiple
  />

const complaintAssignedToTranslated = (value: string): string =>
  t(`complaint:${complaintAssignedToDescription(value)}`)

export const FilterAssignedToComplaint = (props) =>
  <FilterSelector name="assignedTo" {...props}
    menuItems={complaintAssignedTo}
    textAdapter={complaintAssignedToTranslated}
  //    multiple
  />

const complaintInternalAssignedToTranslated = (value: string): string =>
  t(`complaint:${complaintInternalAssignedToDescription(value)}`)

export const FilterInternalAssignedToComplaint = (props) =>
  <FilterSelector name="internalAssignedTo" {...props}
    menuItems={complaintInternalAssignedTo}
    textAdapter={complaintInternalAssignedToTranslated}
  //    multiple
  />

const complaintOwnerOpenByTranslated = (value: string): string =>
  t(`complaint:${complaintOwnerOpenByDescription(value)}`)

export const FilterOwnerOpenByComplaint = (props) =>
  <FilterSelector name="onwerOpenBy" {...props}
    menuItems={complaintOwnerOpenBy}
    textAdapter={complaintOwnerOpenByTranslated}
  //    multiple
  />

export const prepaymentStatusTranslated = (value: string): string =>
  t(`prepayment:${prepaymentStatusToDescription(value)}`)

export const FilterStatusPrepayment = (props) =>
  <FilterSelector name="status" {...props}
    menuItems={prepaymentStatus}
    textAdapter={prepaymentStatusTranslated}
  //    multiple
  />

export const clientStatusTranslated = (value: string): string =>
  t(`client:${clientStatusToDescription(value)}`)

export const FilterStatusClient = (props) =>
  <FilterSelector name="status" {...props}
    menuItems={clientStatus}
    textAdapter={clientStatusTranslated}
  //    multiple
  />

export const productStatusTranslated = (value: string): string =>
  t(`product:${productStatusTypeToDescription(value)}`)

export const FilterStatusProduct = (props) =>
  <FilterSelector name="status" {...props}
    menuItems={productStatusTypes}
    textAdapter={productStatusTranslated}
  //    multiple
  />


const receiptTypeTranslated = (value: string): string => t(`complaint:${receiptTypeToDescription(value)}`)

export const FilterReceipts = (props) =>
  <FilterSelector name="receipt" {...props}
    id={"receipt"}
    menuItems={receiptTypesWithBounced}
    textAdapter={receiptTypeTranslated}
  //    multiple
  />


export const FilterWeekday = (props) => <FilterSelector name="weekday" {...props}
  textAdapter={weekdayToDescription}
  menuItems={range(1, 6)}
/>

export const FilterFromDate = (props) => <FilterDate name="from" timeCorrector={date => moment(date).startOf('day')}{...props} />
export const FilterToDate = (props) => <FilterDate name="to" timeCorrector={date => moment(date).endOf('day')} {...props} />
export const FilterFromMonth = (props) => <FilterMonth name="from" timeCorrector={date => moment(date).startOf('month')} {...props} />

//export const FilterClassTime = (props) => <FilterTime name="time" label="Horario" {...props} />

export const WeekFilterButtonsBar = () => {
  return (
    <ButtonGroup color="primary" variant="outlined" aria-label="outlined primary button group">
      <WeekFilterButton from={currentFromFilter().subtract(1, 'week')} text='Semana anterior'></WeekFilterButton>
      <WeekFilterButton from={moment()} text='Semana actual'></WeekFilterButton>
      <WeekFilterButton from={currentFromFilter().add(1, 'week')} text='Semana siguiente'></WeekFilterButton>
    </ButtonGroup>
  )
}

